import React from "react";

import {
  Document,
  Font,
  Image,
  Line,
  Page,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";

import pdfLogo from "../../../../assets/images/pdfLogo.png";
import myBirthPlan from "../../../../assets/images/mojPlanPoroda.png";
import crossed from "../../../../assets/images/crossed.png";
import poppinsBold from "../../../../assets/font/Poppins-Bold.ttf";
import poppinsLight from "../../../../assets/font/Poppins-Light.ttf";
import poppinsItalic from "../../../../assets/font/Poppins-Italic.ttf";
import poppinsRegular from "../../../../assets/font/Poppins-Regular.ttf";
import {
  BirthPlan,
  BirthPlanCategory,
  BirthPlanQuestion,
  BirthPlanSubcategory,
} from "../BirthPlanForm/types";

Font.register({
  family: "Poppins",
  fonts: [
    { src: poppinsRegular, fontWeight: 400 },
    { src: poppinsBold, fontWeight: 700 },
    { src: poppinsLight, fontWeight: 300 },
    { src: poppinsItalic, fontStyle: "italic" },
  ],
});

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  title: {
    fontSize: 16,
    marginRight: 12,
    marginLeft: 12,
    marginTop: 12,
    marginBottom: 6,
    color: "#000",
    fontWeight: 300,
    fontFamily: "Poppins",
  },
  subtitle: {
    fontSize: 10,
    fontFamily: "Poppins",
    color: "#000",
    marginTop: 5,
    marginHorizontal: 12,
  },
  text: {
    marginLeft: 12,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Poppins",
    color: "#000",
  },
  label: {
    marginLeft: 12,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    fontFamily: "Poppins",
    color: "#000",
  },
  textLeft: {
    marginLeft: 12,
    marginRight: 12,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Poppins",
    color: "#000",
  },
  textBottom: {
    margin: 12,
    marginRight: 20,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Poppins",
    color: "grey",
    fontStyle: "italic",
  },
  date: {
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Poppins",
    color: "grey",
    marginVertical: 10,
  },
  imageLeft: {
    marginHorizontal: 0,
    width: 120,
    height: 30,
  },
  imageRight: {
    marginTop: 20,
    width: 240,
    height: 14,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginRight: 28,
    marginLeft: 12,
  },
  secondHeader: {
    flexDirection: "row",
    marginRight: 18,
    justifyContent: "space-between",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 10,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
    fontWeight: 400,
    fontFamily: "Poppins",
  },
  line: {
    marginTop: 28,
  },
  icon: {
    width: 10,
    marginVertical: 6,
    marginLeft: 12,
    marginRight: -8,
  },
});

export function createField(question: BirthPlanQuestion) {
  const bullet = "- ";

  switch (question.type) {
    case "TEXT":
      return (
        <View style={styles.row}>
          <Text style={styles.label}>{bullet + question.label}</Text>
          <Text style={styles.text}>{question.value}</Text>
        </View>
      );
    case "DATE":
      const date = new Date(Date.parse(question.value));
      return (
        <View style={styles.row}>
          <Text style={styles.label}>{bullet + question.label}</Text>
          <Text style={styles.text}>{date.toLocaleDateString("hr-HR")}</Text>
        </View>
      );
    case "RADIO":
      return (
        <View>
          <Text style={styles.textLeft}>{bullet + question.value}</Text>
        </View>
      );
    case "DROPDOWN":
      return (
        <View style={styles.row}>
          <Text style={styles.text}>{bullet + question.value}</Text>
        </View>
      );
    case "CHECKBOX":
      const x =
        question.name == "hospital-admission-enema-no" ||
        question.name == "hospital-admission-shaving-no" ||
        question.name == "hospital-admission-cannula-no";
      return (
        <View style={styles.row}>
          {x ? (
            <>
              <Image style={styles.icon} src={crossed} />
              <Text style={styles.textLeft}>{question.label}</Text>
            </>
          ) : (
            <Text style={styles.textLeft}>{bullet + question.label}</Text>
          )}
        </View>
      );
    default:
      break;
  }
}

type BirthPlanPdfProps = {
  plan: BirthPlan;
};

export default function BirthPlanPdf({ plan }: BirthPlanPdfProps) {
  function isSubcategoryBlank(subcategory: BirthPlanSubcategory): boolean {
    return !subcategory.questions.some(
      (question) => question.value.toString().length
    );
  }

  function isCategoryBlank(category: BirthPlanCategory): boolean {
    return category.subcategories.every((subcategory) =>
      isSubcategoryBlank(subcategory)
    );
  }

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <View style={styles.secondHeader}>
          <Svg height="10" width="200" style={styles.line}>
            <Line
              x1="10"
              y1="0"
              x2="200"
              y2="0"
              strokeWidth={1}
              stroke="rgb(230,102,171,255)"
            />
          </Svg>
          <Image style={styles.imageRight} src={myBirthPlan} />
        </View>
        <View>
          {plan.categories.map((category) => (
            <>
              {category.subcategories.map((subcategory) => (
                <>
                  {subcategory.subcategory == null &&
                    !isCategoryBlank(category) && (
                      <Text style={styles.title} minPresenceAhead={8}>
                        {category.category}
                      </Text>
                    )}
                  {subcategory.subcategory && !isSubcategoryBlank(subcategory) && (
                    <>
                      <Text style={styles.subtitle}>
                        {subcategory.subcategory}
                      </Text>
                    </>
                  )}
                  {subcategory.questions.map((question) =>
                    question.value.toString().length &&
                    question.value.toString() != "false" ? (
                      <>{createField(question)}</>
                    ) : (
                      <></>
                    )
                  )}
                </>
              ))}
            </>
          ))}
        </View>
        <Text style={styles.textBottom}>
          U ovom planu poroda navela sam svoje želje. Prije ispunjavanja plana
          educirala sam se i spremna sam aktivno sudjelovati kako bi moje želje
          bile ispunjene u što većoj mjeri. Svjesna sam da se porođaj i skrb za
          dijete mogu zakomplicirati i da će u tom slučaju doći do nužne
          promjene u planu poroda.
        </Text>
        <Svg height="20" width="500">
          <Line
            x1="10"
            y1="0"
            x2="500"
            y2="0"
            strokeWidth={1}
            stroke="rgb(230,102,171,255)"
          />
        </Svg>
        <View style={styles.header}>
          <Image style={styles.imageLeft} src={pdfLogo} />
          <Text style={styles.date}>
            {new Date().toLocaleString("hr-HR", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </Text>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
